/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { ICartState } from '@msdyn365-commerce/global-state';
import * as React from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { IProductCollectionViewProps, IProductComponentViewProps } from '@msdyn365-commerce-modules/product-collection';
import { IProductCollectionData } from './product-collection.data';
import { IProductCollectionProps, IProductCollectionResources } from '../definition-extensions/product-collection.ext.props.autogenerated';
import { CategoryPathResult, CommerceList, CommerceListsDataActions, Customer, ProductList, ProductsDataActions, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    OrderTemplate,
    QueryResultSettingsProxy,
    GetOrderTemplatesInput,
    getOrderTemplates,
    GetCustomerInput,
    getCustomer,
    buildWishlistLine
} from '@msdyn365-commerce-modules/retail-actions';
import ProductComponent, { IAddToOrderTemplateResources, IProductComponentProps } from './components/product.component';
import PQueue from 'p-queue';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getProductUnitOfMeasureOptionsAsync } from '../../../actions/get-product-unit-of-measure-options.action';
import { DeliveryMode, IProductInventoryInformation, createInventoryAvailabilitySearchCriteria, mapAggregatedProductInventoryInformation } from '../../../components/common/product-inventory-utils';
import { onBuyHasAccessValidator } from '../../../shared-utils/attributes/on-buy-access-validator';
import { useActivePrices } from '../../../shared-utils/hooks/use-active-prices';
import { getDiscountBadge } from '../../../shared-utils/badge-utils';
import { applyAttributesToProduct, hasAccessToBuy } from '../../../shared-utils/attributes';
import { toast, toasts } from '../../../components/toast/toast';
import { getCustomerAgreements } from '../../../shared-utils/agreement-product-utils';
import { ProductSearchResultExt } from '../../../shared-utils/types';
import { getProductListsByProductIdsAsync, IProductListByProductId } from '../../../actions/get-product-lists-by-product-ids';

function getInventoryLabel(productRecordId: number, productInventoryStatus: IProductInventoryInformation[] | undefined): string | undefined {
    if(productInventoryStatus){
        const shippingInventory = productInventoryStatus.find(shipping => shipping.ProductAvailableQuantity.ProductId === productRecordId);
        if(shippingInventory && shippingInventory.StockLevelLabel){
            return shippingInventory.StockLevelLabel;
        }
    }

    return undefined;
}

const useGetInventoryStatus = (props: IProductCollectionViewProps & IProductCollectionProps<IProductCollectionData>) => {
    const { config, context } = props;
    const [productInventoryStatus, setProductInventoryStatus] = React.useState<IProductInventoryInformation[] | undefined>();

    React.useEffect(() => {
        if (!config.productCollection?.products || config.productCollection.products.length === 0) {
            return;
        }

        const work = async () => {
            const shippingSearchCriteria = createInventoryAvailabilitySearchCriteria(
                context.actionContext,
                config.productCollection.products.map(x => x.RecordId),
                true,
                undefined,
                undefined,
                DeliveryMode.shipping
            );

            const shippingAvailability = await ProductsDataActions.getEstimatedAvailabilityAsync(
                { callerContext: context.actionContext },
                shippingSearchCriteria
            );

            if (shippingAvailability.ProductWarehouseInventoryAvailabilities) {
                const shippingInventory = mapAggregatedProductInventoryInformation(context.actionContext, shippingAvailability);

                if (ArrayExtensions.hasElements(shippingInventory)) {
                    shippingInventory[0].deliveryType = DeliveryMode.shipping;
                    setProductInventoryStatus(shippingInventory);
                }
            }
        };
        void work();
    }, [config.productCollection]);

    return productInventoryStatus;
};

const ProductCollectionView: React.FC<IProductCollectionViewProps & IProductCollectionProps<IProductCollectionData>> = props => {
    const {
        context,
        heading,
        ProductCollectionContainer,
        products,
        SingleSlideCarouselComponentProps,
        GridComponentProps,
        isCarousel,
        seeAllButton,
        resources,
        data: { cart, customerInformation, orgInformation, wishlists }
    } = props;
    const [orderTemplates, setOrderTemplates] = React.useState<OrderTemplate[] | undefined>(undefined);
    const cartQueue = React.useMemo(() => new PQueue({ concurrency: 1 }), []);
    const [simpleProducts, setSimpleProducts] = React.useState<SimpleProduct[] | undefined>(undefined);
    const [unitConversions, setConversions] = React.useState<UnitOfMeasureConversion[]>([]);
    const [categoryPaths, setCategoryPaths] = React.useState<CategoryPathResult[] | undefined>(undefined);
    const productInventoryStatus = useGetInventoryStatus(props);

    React.useEffect(() => {
        const { actionContext, request, app } = props.context;
        const ids = props.config.productCollection?.products?.map(x => x.RecordId) as number[] | undefined;

        if (!ids || ids.length === 0) {
            return;
        }

        if (ids.some(x => !simpleProducts?.find(y => y.RecordId === x))) {
            const work = async () => {
                const simpleProducts = await getByIdsAsync(
                    { callerContext: actionContext },
                    request.apiSettings.channelId,
                    ids,
                    null,
                    request.apiSettings.catalogId);
                setSimpleProducts(simpleProducts);
            };
            void work();
        }

        if (request.user.isAuthenticated && app.config.unitOfMeasureConversions) {
            const work = async () => {
                const conversions = await getProductUnitOfMeasureOptionsAsync({ callerContext: actionContext }, ids);
                setConversions(conversions);
            };
            void work();
        }

        if (request.user.isAuthenticated) {
            const work = async () => {
                const productListsByProductIds = await getProductListsByProductIdsAsync({ callerContext: actionContext }, ids);
                (props.config.productCollection.products as ProductSearchResultExt[]).forEach(product => {
                    product.PartOfProductLists = productListsByProductIds.filter(x => x.ProductId === product.RecordId);
                });
            };
            void work();
        }

        (async () => {
            const categoryPaths = await ProductsDataActions.getCategoryPathsAsync({ callerContext: context.actionContext },
                context.request.apiSettings.channelId,
                context.request.apiSettings.catalogId,
                ids.map(id => ({ ProductId: id }))
            );
            setCategoryPaths(categoryPaths);
        })();
    }, [props.config.productCollection]);

    const [activePrices] = useActivePrices(context, props.config.productCollection?.products?.map(x => x.RecordId));

    React.useEffect(() => {
        if (!Array.isArray(products)) {
            return;
        }

        products?.forEach((product) => {
            const typedProduct = ((product.productComponent as React.ReactElement).props.data.product as ProductSearchResultExt);
            const price = activePrices.find(x => x.ProductId === typedProduct.RecordId);
    
            product.productComponent = React.cloneElement(product.productComponent as React.ReactElement, {
                showPrice: !!price
            });

            const categoryPath = categoryPaths?.find(cp => cp.ProductId === typedProduct.RecordId)?.CategoryPath;

            applyAttributesToProduct(context.request, typedProduct, typedProduct.AttributeValues, categoryPath, !!categoryPaths);

            typedProduct.Price = price?.CustomerContextualPrice ?? 0;
            typedProduct.BasePrice = price?.BasePrice ?? 0;
            typedProduct.MaxVariantPrice = price?.MaxVariantPrice ?? 0;
            typedProduct.MinVariantPrice = price?.MinVariantPrice ?? 0;
            typedProduct.ProductPrice = price;
            typedProduct.Badge = getDiscountBadge(price, typedProduct.FallbackBadge);
        });
    }, [products, activePrices, categoryPaths]);

    const hasAccessTo = (product: IProductComponentViewProps): boolean => {
        const typedProduct = ((product.productComponent as React.ReactElement).props.data.product as ProductSearchResultExt);

        return hasAccessToBuy(context, orgInformation.result, typedProduct.AttributeValues);
    }

    const updateOrderTemplatesAsync = async (product?: ProductSearchResultExt, newOrderTemplate?: ProductList) => {
        let newOrderTemplates;

        if (newOrderTemplate) {
            newOrderTemplates = [...(orderTemplates ? orderTemplates : [])];

            // Add new order template if it doesn't exist
            if (newOrderTemplates.every(x => x.productList.Id !== newOrderTemplate.Id)) {
                newOrderTemplates.splice(0, 0, new OrderTemplate(newOrderTemplate, [], 0));
            }

            if (product) {
                const newProducts = [...props.config.productCollection.products];
                const newProduct: ProductSearchResultExt | undefined = newProducts?.find(x => x.RecordId === product.RecordId);

                if (newProduct) {
                    const newPartOfProductList: IProductListByProductId = {
                        ProductId: newProduct.RecordId,
                        ProductListId: newOrderTemplate?.Id,
                        Name: newOrderTemplate?.Name
                    };
                    if (!newProduct.PartOfProductLists) {
                        newProduct.PartOfProductLists = [];
                    }
                    newProduct.PartOfProductLists.push(newPartOfProductList);
                }

                props.config.productCollection.products = newProducts;
            }
        } else {
            // Refresh order templates incase new order template was created
            const queryResultSettings = QueryResultSettingsProxy.fromInputData({
                requestContext: props.context.request,
                config: props.config
            }).QueryResultSettings;
            const input = new GetOrderTemplatesInput('', queryResultSettings);
            newOrderTemplates = (await getOrderTemplates(input, props.context.actionContext)).map(x => new OrderTemplate(x, [], 0));
        }

        setOrderTemplates(newOrderTemplates);
        return newOrderTemplates;
    };

    const retrieveOrderTemplates = async () => {
        return orderTemplates ? orderTemplates : await updateOrderTemplatesAsync();
    };

    React.useEffect(() => {
        if (!props.config.autoplay) {
            return;
        }

        if (props.id !== 'product-collection__2') {
            return;
        }

        const nextFlipperId = `flipperNextButton_${props.id}`;
        const prevFlipperId = `flipperPreviousButton_${props.id}`;
        const nextFlipper = document.getElementById(nextFlipperId) as HTMLButtonElement;
        const prevFlipper = document.getElementById(prevFlipperId) as HTMLButtonElement;
        const carousel = prevFlipper.closest('.msc-ss-carousel');

        const intervalMS = props.config.interval || 5000;
        const onAutoPlay = () => {
            if (!nextFlipper.disabled) {
                nextFlipper.click();
            } else if (!prevFlipper.disabled) {
                prevFlipper.click();
            }
        };
        let interval = setInterval(onAutoPlay, intervalMS);

        if (props.config.pauseOnHover) {
            carousel?.addEventListener('mouseover', () => {
                clearInterval(interval);
            });
            carousel?.addEventListener('mouseleave', () => {
                interval = setInterval(onAutoPlay, intervalMS);
            });
        }
    }, []);

    if (products) {
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {seeAllButton}
                {isCarousel
                    ? _renderCarousel(context,
                        SingleSlideCarouselComponentProps,
                        products.filter(hasAccessTo),
                        cart.result,
                        customerInformation.result,
                        orgInformation.result,
                        unitConversions,
                        wishlists.result,
                        cartQueue,
                        retrieveOrderTemplates,
                        updateOrderTemplatesAsync,
                        resources,
                        simpleProducts,
                        productInventoryStatus)
                    : _renderGrid(context,
                        GridComponentProps,
                        products.filter(hasAccessTo),
                        cart.result,
                        customerInformation.result,
                        orgInformation.result,
                        unitConversions,
                        wishlists.result,
                        cartQueue,
                        retrieveOrderTemplates,
                        updateOrderTemplatesAsync,
                        resources,
                        simpleProducts,
                        productInventoryStatus)}
            </Module>
        );
    }

    // If no products are retrieved and the list type is promotional, still render the module to keep the entry of promotion page
    if (props.config.productCollection.listType === 'promotional') {
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {seeAllButton}
                {_renderNoProduct(resources)}
            </Module>
        );
    }
    props.context.telemetry.error('Product collection content is empty, module wont render.');
    return null;
};

const _renderCarousel = (
    context: ICoreContext,
    carouselContainer: INodeProps,
    items: IProductComponentViewProps[],
    cart: ICartState | undefined,
    customerInformation: Customer | undefined,
    orgInformation: Customer | undefined,
    unitConversions: UnitOfMeasureConversion[],
    wishlists: CommerceList[] | undefined,
    cartQueue: PQueue,
    retrieveOrderTemplates: () => Promise<OrderTemplate[]>,
    onUpdateOrderTemplates: ((product?: ProductSearchResultExt, orderTemplate?: ProductList | undefined) => Promise<OrderTemplate[]>) | undefined,
    resources: IProductCollectionResources,
    simpleProducts: SimpleProduct[] | undefined,
    productInventoryStatus: IProductInventoryInformation[] | undefined
): JSX.Element => {
    return (
        <Node {...carouselContainer}>
            {items && items.map(x => _renderProduct(
                context,
                x,
                simpleProducts?.find(sp => (x.productComponent as React.ReactElement).props.data.product.RecordId === sp.RecordId),
                cart,
                customerInformation,
                orgInformation,
                unitConversions,
                wishlists,
                productInventoryStatus?.find(pi => pi.ProductAvailableQuantity.ProductId === (x.productComponent as React.ReactElement).props.data.product.RecordId),
                cartQueue,
                retrieveOrderTemplates,
                onUpdateOrderTemplates,
                resources))}
        </Node>
    );
};

const _renderGrid = (
    context: ICoreContext,
    gridContainer: INodeProps,
    items: IProductComponentViewProps[],
    cart: ICartState | undefined,
    customerInformation: Customer | undefined,
    orgInformation: Customer | undefined,
    unitConversions: UnitOfMeasureConversion[],
    wishlists: CommerceList[] | undefined,
    cartQueue: PQueue,
    retrieveOrderTemplates: () => Promise<OrderTemplate[]>,
    onUpdateOrderTemplates: ((product?: ProductSearchResultExt, orderTemplate?: ProductList | undefined) => Promise<OrderTemplate[]>) | undefined,
    resources: IProductCollectionResources,
    simpleProducts: SimpleProduct[] | undefined,
    productInventoryStatus: IProductInventoryInformation[] | undefined
): JSX.Element => {
    return (
        <Node {...gridContainer}>
            {items && items.map(x => _renderProduct(
                context,
                x,
                simpleProducts?.find(sp => (x.productComponent as React.ReactElement).props.data.product.RecordId === sp.RecordId),
                cart,
                customerInformation,
                orgInformation,
                unitConversions,
                wishlists,
                productInventoryStatus?.find(pi => pi.ProductAvailableQuantity.ProductId === (x.productComponent as React.ReactElement).props.data.product.RecordId),
                cartQueue,
                retrieveOrderTemplates,
                onUpdateOrderTemplates,
                resources))}
        </Node>
    );
};

const _renderNoProduct = (resources: IProductCollectionResources): JSX.Element => {
    return (
        <Node className={''}>
            <p>{resources.noProductToRenderText}</p>
        </Node>
    );
};

const _renderProduct = (
    context: ICoreContext,
    product: IProductComponentViewProps,
    simpleProduct: SimpleProduct | undefined,
    cart: ICartState | undefined,
    customerInformation: Customer | undefined,
    orgInformation: Customer | undefined,
    unitConversions: UnitOfMeasureConversion[],
    wishlists: CommerceList[] | undefined,
    productInventoryStatus: IProductInventoryInformation | undefined,
    cartQueue: PQueue,
    retrieveOrderTemplates: () => Promise<OrderTemplate[]>,
    onUpdateOrderTemplates: ((product?: ProductSearchResultExt, orderTemplate?: ProductList | undefined) => Promise<OrderTemplate[]>) | undefined,
    resources: IProductCollectionResources
): JSX.Element => {
    const { ProductContainer, productComponent } = product;

    const data = {
        ...(productComponent as React.ReactElement).props.data,
        cart,
        customerInformation,
        customerAgreements: getCustomerAgreements(context, orgInformation),
        orgInformation,
        simpleProduct,
        unitConversions: unitConversions.filter(x => x.ProductRecordId === (productComponent as React.ReactElement).props.data.product?.RecordId)
    };

    const props: Partial<IProductComponentProps> = {
        data,
        depositSuffixText: resources.depositSuffixText,
        cartQueue,
        inventoryLabel: getInventoryLabel((productComponent as React.ReactElement).props.data.product?.RecordId, productInventoryStatus ? [productInventoryStatus] : undefined),
        inventoryStatus: productInventoryStatus,
        wishlistButton: _renderWishlistButton(context, product, resources, wishlists),
        retrieveOrderTemplates: retrieveOrderTemplates,
        onUpdateOrderTemplates: onUpdateOrderTemplates,
        addToCartText: resources.addToCartButtonText,
        updateCartText: resources.updateCartText,
        removeFromCartText: resources.removeFromCartText,
        priceErrorText: resources.priceErrorText,
        inCartTooltipText: resources.inCartTooltipText,
        transitTooltip: resources.transitTooltip,
        priceNotLoggedInText: resources.priceNotLoggedInText,
        agreementProductTooltip: resources.agreementProductTooltip,
        toastStrings: {
            errorHeader: resources.toastErrorHeader,
            productsAddedToCart: resources.productsAddedToCart,
            productsRemovedFromCart: resources.productsRemovedFromCart,
            productsUpdatedInCart: resources.productsUpdatedInCart,
            goToCart: resources.toastGoToCart,
            noAccessToProduct: resources.toastNoAccessToProduct
        },
        addToOrderTemplateResources: {
            addToOrderTemplateHeader: resources.addToOrderTemplateHeader,
            noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
            noOrderTemplatesDescription: resources.noOrderTemplatesDescription,
            createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
            createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
            cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,
            selectTemplatesText: resources.selectTemplatesText,
            addToTemplateButtonText: resources.addToTemplateButtonText,
            lineItemsText: resources.lineItemsText,
            createOrderTemplateHeader: resources.createOrderTemplateHeader,
            orderTemplateTitle: resources.orderTemplateTitle,
            orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
            createOrderTemplateDescription: resources.createOrderTemplateDescription,
            defaultOrderTemplateName: resources.defaultOrderTemplateName,
            createOrderTemplateButtonText: resources.createOrderTemplateButtonText,
            cancelNewOrderTemplateCreationButtonText: resources.cancelNewOrderTemplateCreationButtonText,
            viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
            continueShoppingButtonText: resources.continueShoppingButtonText,
            itemAddedToOrderTemplateHeaderItemOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
            itemAddedToOrderTemplateHeaderItemFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
            itemAddedToOrderTemplateHeaderMessageText: resources.itemAddedToOrderTemplateHeaderMessageText,
            priceFree: resources.priceFree,
            originalPriceText: resources.originalPriceText,
            currentPriceText: resources.currentPriceText,
            duplicatedProductsHeader: resources.duplicatedProductsHeader,
            duplicatedProductsDescription: resources.duplicatedProductsDescription,
            updateQuantityButtonText: resources.updateQuantityButtonText,
            cancelDuplicateItemsButtonText: resources.cancelDuplicateItemsButtonText,
            addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
            addToOrderTemplateButtonTooltip: resources.addToOrderTemplateButtonTooltip
        } as IAddToOrderTemplateResources,
        onBuyValidate: (product) => onBuyHasAccessValidator(
            context,
            customerInformation,
            product,
            resources.toastNoAccessToProduct
        )
    }

    // TODO: Hopefully go back to this
    // const extendedProductComponent = React.cloneElement(productComponent as React.ReactElement, props);

    // Force the component to use our override since overrides are not always working automatically atm.
    // Directly references our ProductComponent instead of the one passed in.
    const forcedComponentProps = {
        ...(productComponent as React.ReactElement).props,
        ...props
    };
    const forcedComponent = <ProductComponent {...forcedComponentProps} />;

    return <Node {...ProductContainer}>{forcedComponent}</Node>;
};

const _renderWishlistButton = (
    context: ICoreContext,
    productViewProps: IProductComponentViewProps,
    resources: IProductCollectionResources,
    wishlists: CommerceList[] | undefined
): JSX.Element => {
    const product = (productViewProps.productComponent as React.ReactElement).props.data.product as ProductSearchResultExt;
    const isWishlisted = wishlists && wishlists.length > 0 && wishlists[0].CommerceListLines?.find(x => x.ProductId === product.RecordId) ? true : false;
    let className = 'btn-add-to-wishlist';

    if (isWishlisted) {
        className += ' is-wishlisted';
    }

    return (
        <div>
            <button
                className={className}
                title={!isWishlisted ? resources.addToWishlistButtonText : resources.removeFromWishlistButtonText}
                aria-label={!isWishlisted ? resources.addToWishlistButtonText : resources.removeFromWishlistButtonText}
                onClick={async () => {
                    if (!isWishlisted) {
                        await _addItemToWishlist(context, resources, product, wishlists);
                    } else {
                        await _removeItemFromWishlist(context, resources, product, wishlists);
                    }
                }
            } />
        </div>
    )
};

const _addItemToWishlist = async (
    context: ICoreContext,
    resources: IProductCollectionResources,
    product: ProductSearchResultExt,
    wishlists: CommerceList[] | undefined
) => {
    if (!context || !product || !resources || !context.request.user.isAuthenticated) {
        return;
    }

    const customerInput = new GetCustomerInput(context.actionContext.requestContext.apiSettings);
    const customer = await getCustomer(customerInput, context.actionContext);

    if (!customer || !customer.AccountNumber) {
        return;
    }

    const listData: CommerceList = {
        CustomerId: customer.AccountNumber,
        Name: resources.defaultWishlistName,
        CommerceListTypeValue: 1,
        Id: 0
    };

    let wishlist = wishlists?.[0];

    if (!wishlist) {
        try {
            const commerceList = await CommerceListsDataActions.createCommerceListAsync(
                { callerContext: context.actionContext },
                listData);
            wishlist = commerceList;
        } catch (err) {
            const error = err as any;
            const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
            toasts.error(resources.toastErrorHeader, error, { footer });
            return;
        }
    }

    try {
        const createdCommerceList = await CommerceListsDataActions.addLinesAsync({ callerContext: context.actionContext },
            wishlist.Id,
            [
                buildWishlistLine(product.RecordId, customer.AccountNumber, wishlist.Id)
            ]);
        context.actionContext.update(CommerceListsDataActions.createGetByCustomerInput({}, null), [createdCommerceList]);

        toast({
            text: resources.addedProductToWishlistText.replace('{productName}', product[0].Name || ''),
            type: 'success',
            autoClose: 3000,
            position: 'bottom-right',
            canClose: true,
            showProgress: false
        });
    } catch (error) {
        const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
        toasts.error(resources.toastErrorHeader, error, { footer });
    }
};

const _removeItemFromWishlist = async (
    context: ICoreContext,
    resources: IProductCollectionResources,
    product: ProductSearchResultExt,
    wishlists: CommerceList[] | undefined
) => {
    const wishlist = wishlists?.[0];
    const wishlistLineId = wishlist?.CommerceListLines?.find(x => x.ProductId === product.RecordId)?.LineId;

    if (!wishlists || !wishlist || !wishlistLineId) {
        return;
    }

    try {
        const updatedCommerceList = await CommerceListsDataActions.removeLinesAsync(
            { callerContext: context.actionContext },
            wishlist.Id,
            [{ LineId: wishlistLineId }]);

        const newWishlists = [ ...wishlists ];
        newWishlists[0] = updatedCommerceList;
        context.actionContext.update(CommerceListsDataActions.createGetByCustomerInput({}, null), newWishlists);
    } catch (err) {
        const error = err as any;
        const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
        toasts.error(resources.toastErrorHeader, error, { footer });
    }
};

export default ProductCollectionView;
